import { Suspense, lazy, useEffect } from "react";
import {
  Navigate,
  useLocation,
  useRoutes,
  useNavigate,
} from "react-router-dom";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
import DashboardLayout from "../layouts/dashboard";
import AuthGuard from "../guards/AuthGuard";
import GuestGuard from "../guards/GuestGuard";
import LoadingScreen from "../components/LoadingScreen";
import useNavigationHistory from "../hooks/useNavigationHistory";
import { PATH_DASHBOARD, PATH_AUTH } from "./paths";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes("/dashboard");

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: "fixed",
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { currentPath, grandPreviousPath, parentPreviousPath } =
    useNavigationHistory();
  const navigate = useNavigate();

  const matchInvoiceListPath = (path) => {
    if (!path || typeof path !== "string") return { isMatch: false };
    const regex = /^\/invoice\/list\/(\d+)\/page-(\d+)$/;
    const match = path.match(regex);
    return match
      ? { isMatch: true, tab: match[1], pages: match[2] }
      : { isMatch: false };
  };

  const matchInvoiceEditPath = (path) => {
    if (!path || typeof path !== "string") return { isMatch: false };
    const regex = /^\/invoice\/(\d+)\/edit$/;
    const match = path.match(regex);
    return match ? { isMatch: true, id: match[1] } : { isMatch: false };
  };

  const isCurrentPathInvoiceList = matchInvoiceListPath(currentPath).isMatch;
  const grandPreviousMatch = matchInvoiceEditPath(grandPreviousPath);
  const parentPreviousMatch = matchInvoiceListPath(parentPreviousPath);

  const { tab, pages } = matchInvoiceListPath(parentPreviousPath);

  const invoiceListComponent =
    isCurrentPathInvoiceList &&
    grandPreviousMatch.isMatch &&
    parentPreviousMatch;

  useEffect(() => {
    const destination = PATH_DASHBOARD.invoice.list
      .replace(":tab", tab)
      .replace(":pages", pages);

    // Prevent navigating to the same route
    if (invoiceListComponent?.isMatch && currentPath !== destination) {
      navigate(destination, { replace: true });
    }
  }, [invoiceListComponent, tab, pages, currentPath, navigate]);

  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: "login-unprotected", element: <Login /> },
      ],
    },
    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.general.app} replace />,
        },
        { path: "app", element: <GeneralApp /> },
      ],
    },
    {
      path: "invoice",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: (
            <Navigate
              to={PATH_DASHBOARD.invoice.list
                .replace(":tab", "6")
                .replace(":pages", 0)}
              replace
            />
          ),
        },
        {
          path: "list/:tab/page-:pages",
          element: <InvoiceList />,
        },
        { path: "customer/new", element: <CustomerCreate /> },
        { path: "/:id/edit", element: <InvoiceCreate /> },
        { path: "/:id/invoicepdf", element: <MainInvoicePdf /> },
        { path: "/:id/:proId/valuation", element: <ValuationView /> },
        { path: "price-calculator", element: <InvoicePage /> },
        { path: "/price-calculator/features", element: <QuotationItemList /> },       

      ],
    },
        {
      path: "price-calculator",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        
        { path: "preview", element: <InvoicePage /> },
        { path: "features", element: <QuotationItemList /> },       

      ],
    },
    
    {
      path: "manufacture",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.manufacture.list} replace />,
        },
        { path: "list", element: <ManufacturerList /> },
        { path: "remakelist", element: <ManufactureRemakeList /> },
        { path: "resizelist", element: <ManufactureResizeList /> },
        { path: "repairlist", element: <ManufactureRepairList /> },
        { path: "master-card-list", element: <MasterCardList /> },
        { path: "/:id/view", element: <ManufactureCreate /> },
        { path: "notification-list", element: <NotificationInvoiceList /> },
        { path: "notification/:id/view", element: <NotificationCreate /> },
      ],
    },
    {
      path: "lead",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.lead.newLeader} replace />,
        },
        // { path: 'list', element: <LeaderFormView /> },
        { path: "add", element: <LeaderFormView /> },
        { path: "/:id/view", element: <LeaderCreate /> },
        { path: "/:id/quotation", element: <MainQuotationView /> },
        { path: "all", element: <LeaderList /> },
        { path: "calendar", element: <LeaderCalender /> },
      ],
    },
    {
      path: "cost",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.cost.postProd} replace />,
        },
        { path: "post-production", element: <CostList /> },
        { path: "expenses", element: <ExpensesList /> },
      ],
    },
    {
      path: "master",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.master.user} replace />,
        },
        { path: "user/list", element: <UserList /> },
        { path: "user/profile", element: <UserProfile /> },
        { path: "user/:id/profile", element: <UserProfile /> },
        { path: "store", element: <StoreList /> },
        { path: "design-master", element: <DesignDetailsList /> },
        { path: "workshop", element: <WorkShopList /> },
      ],
    },
    {
      path: "task",

      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.task.root} replace />,
        },
        { path: "invoice", element: <TaskList /> },
        { path: "balance-reminder", element: <SellerTaskList /> },
        { path: "workshop", element: <WorkShopTaskList /> },
      ],
    },
    {
      path: "inventory",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // Lab Grown routes
        { path: "lab-grown", element: <InventoryDiamondList /> },
        { path: "lab-grown/diamond", element: <InventoryDiamondList /> },
        { path: "lab-grown/gemstone", element: <InventoryGemstoneList /> },
        { path: "lab-grown/mount", element: <InventoryUnmountList /> },
        { path: "lab-grown/finish-ring", element: <InventorySetmountList /> },
        { path: "lab-grown/earrings", element: <InventoryEarringsList /> },
        { path: "lab-grown/pendants", element: <InventoryPendantsList /> },
        { path: "lab-grown/bracelet", element: <InventoryBraceletList /> },
        // Natural routes
        { path: "natural", element: <InventoryNaturalDiamondList /> },
        { path: "natural/diamond", element: <InventoryNaturalDiamondList /> },
        { path: "natural/gemstone", element: <InventoryNaturalGemstoneList /> },
        { path: "natural/mount", element: <InventoryNaturalUnmountList /> },
        {
          path: "natural/finish-ring",
          element: <InventoryNaturalSetmountList />,
        },
        { path: "natural/earrings", element: <InventoryNaturalEarringsList /> },
        { path: "natural/pendants", element: <InventoryNaturalPendantsList /> },
        { path: "natural/bracelet", element: <InventoryNaturalBraceletList /> },
        // Redirect
        {
          index: true,
          element: (
            <Navigate
              to={`${PATH_DASHBOARD.inventory.labgrown.root}`}
              replace
            />
          ),
        },
      ],
    },
    {
      path: "report",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: "invoice/daily", element: <ReportList /> },
        { path: "invoice/monthly", element: <MonthlyReportList /> },
        { path: "invoice/annual", element: <AnnualReportsList /> },
        { path: "payment/daily", element: <PaymentDailyReportList /> },
        {
          path: "payment/monthly",
          element: <PaymentMonthlyReportList />,
        },
        { path: "payment/annual", element: <PaymentAnnualReportList /> },
        { path: "leaderboard/daily", element: <SalesReportList /> },
        {
          path: "leaderboard/monthly",
          element: <MonthlySalesReportList />,
        },
        {
          path: "leaderboard/annual",
          element: <AnnualSalesReportList />,
        },
         { path: "leaderboard/payable-commission/daily", element: <DailyPayableReportList /> },
        {
          path: "leaderboard/payable-commission/monthly",
          element: <MonthlyPayableReportList />,
        },
        {
          path: "leaderboard/payable-commission/annual",
          element: <AnnualPayableReportList />,
        },
        { path: "leaderboard/calendar", element: <Calendar /> },
        { path: "cost/all-months", element: <AllMonthCostList /> },
        { path: "cost/annual", element: <AnnualCostList /> },
        { path: "data/daily", element: <DataReportsList /> },
        { path: "data/monthly", element: <MonthlyDataReports /> },
        { path: "data/annual", element: <AnnualDataReports /> },
        {
          path: "data/engagement-ring",
          element: <EngagementRingReport />,
        },
      ],
    },

    {
      path: "stock",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.stock.list} replace />,
        },
        { path: "list", element: <StockList /> },
        { path: "/new", element: <StockItemsCreate /> },
        { path: "/:id/view", element: <StockCreate /> },
      ],
    },
    {
      path: "product",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to={PATH_DASHBOARD.product.list} replace />,
        },
        { path: "list", element: <ProductList /> },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "coming-soon", element: <ComingSoon /> },
        { path: "maintenance", element: <Maintenance /> },
        { path: "500", element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/confirmation-order/:orderId",
      element: <PageOrderConfirmation />,
    },
    {
      path: "/",
      element: <Navigate to={PATH_AUTH.login} />,
      children: [
        {
          path: "components",
          children: [
            // MATERIAL UI
            { path: "alert", element: <Alert /> },
            { path: "autocomplete", element: <Autocomplete /> },
            { path: "avatar", element: <Avatar /> },
            { path: "badge", element: <Badge /> },
            { path: "breadcrumbs", element: <Breadcrumb /> },
            { path: "buttons", element: <Buttons /> },
            { path: "checkbox", element: <Checkbox /> },
            { path: "chip", element: <Chip /> },
            { path: "dialog", element: <Dialog /> },
            { path: "label", element: <Label /> },
            { path: "list", element: <List /> },
            { path: "menu", element: <Menu /> },
            { path: "pagination", element: <Pagination /> },
            { path: "pickers", element: <Pickers /> },
            { path: "popover", element: <Popover /> },
            { path: "progress", element: <Progress /> },
            { path: "radio-button", element: <RadioButtons /> },
            { path: "rating", element: <Rating /> },
            { path: "slider", element: <Slider /> },
            { path: "snackbar", element: <Snackbar /> },
            { path: "stepper", element: <Stepper /> },
            { path: "switch", element: <Switches /> },
            { path: "table", element: <Table /> },
            { path: "tabs", element: <Tabs /> },
            { path: "textfield", element: <Textfield /> },
            { path: "timeline", element: <Timeline /> },
            { path: "tooltip", element: <Tooltip /> },
            { path: "transfer-list", element: <TransferList /> },
            { path: "tree-view", element: <TreeView /> },
          ],
        },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS
// Authentication
const Login = Loadable(lazy(() => import("../pages/authentication/Login")));
// Dashboard
const GeneralApp = Loadable(lazy(() => import("../pages/GeneralApp")));
// Main
const ComingSoon = Loadable(lazy(() => import("../pages/ComingSoon")));
const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
// Main Module
const UserList = Loadable(lazy(() => import("../pages/UserList")));
const StoreList = Loadable(lazy(() => import("../pages/StoreList")));
const WorkShopList = Loadable(lazy(() => import("../pages/WorkShopList")));
const WorkShopTaskList = Loadable(
  lazy(() => import("../pages/WorkShopTaskList"))
);

const InvoicePage = Loadable(lazy(() => import("../pages/InvoicePage")));
const InvoiceList = Loadable(lazy(() => import("../pages/InvoiceList")));
const CustomerCreate = Loadable(lazy(() => import("../pages/CustomerCreate")));
const StockItemsCreate = Loadable(
  lazy(() => import("../pages/StockItemsCreate"))
);
const QuotationItemList = Loadable(
  lazy(() => import("../pages/QuotationItemList"))
);
const InvoiceCreate = Loadable(lazy(() => import("../pages/InvoiceCreate")));
const MainInvoicePdf = Loadable(lazy(() => import("../pages/MainInvoicePdf")));
const CostList = Loadable(lazy(() => import("../pages/CostList")));
const ExpensesList = Loadable(lazy(() => import("../pages/ExpensesList")));
const AllMonthCostList = Loadable(
  lazy(() => import("../pages/AllMonthCostList"))
);
const AnnualCostList = Loadable(lazy(() => import("../pages/AnnualCostList")));
const SalesReportList = Loadable(
  lazy(() => import("../pages/SalesReportList"))
);
const MonthlySalesReportList = Loadable(
  lazy(() => import("../pages/MonthlySalesReportList"))
);
const AnnualSalesReportList = Loadable(
  lazy(() => import("../pages/AnnualSalesReportList"))
);
const ReportList = Loadable(lazy(() => import("../pages/ReportList")));
const MonthlyReportList = Loadable(
  lazy(() => import("../pages/MonthlyReportList"))
);
const AnnualReportsList = Loadable(
  lazy(() => import("../pages/AnnualReportsList"))
);
const DailyPayableReportList = Loadable(
  lazy(() => import("../pages/DailyPayableReportList"))
);
const MonthlyPayableReportList = Loadable(
  lazy(() => import("../pages/MonthlyPayableReportList"))
);
const AnnualPayableReportList = Loadable(
  lazy(() => import("../pages/AnnualPayableReportList"))
);

const PaymentDailyReportList = Loadable(
  lazy(() => import("../pages/PaymentDailyReportList"))
);
const PaymentMonthlyReportList = Loadable(
  lazy(() => import("../pages/PaymentMonthlyReportList"))
);
const PaymentAnnualReportList = Loadable(
  lazy(() => import("../pages/PaymentAnnualReportList"))
);

const TaskList = Loadable(lazy(() => import("../pages/TaskList")));
const SellerTaskList = Loadable(lazy(() => import("../pages/SellerTaskList")));

const UserProfile = Loadable(lazy(() => import("../pages/UserProfile")));
const StockList = Loadable(lazy(() => import("../pages/StockList")));
const StockCreate = Loadable(lazy(() => import("../pages/StockCreate")));
const ValuationView = Loadable(
  lazy(() => import("../sections/invoice/Valuation/ValuationToolBar"))
);
const ManufacturerList = Loadable(
  lazy(() => import("../pages/ManufacturerList"))
);
const ManufactureCreate = Loadable(
  lazy(() => import("../pages/ManufactureCreate"))
);
const ManufactureResizeList = Loadable(
  lazy(() => import("../pages/ManufactureResizeList"))
);
const ManufactureRepairList = Loadable(
  lazy(() => import("../pages/ManufactureRepairList"))
);
const ManufactureRemakeList = Loadable(
  lazy(() => import("../pages/ManufactureRemakeList"))
);
const NotificationCreate = Loadable(
  lazy(() => import("../pages/NotificationCreate"))
);
const NotificationInvoiceList = Loadable(
  lazy(() => import("../pages/NotificationInvoiceList"))
);
const PageOrderConfirmation = Loadable(
  lazy(() => import("../pages/PageOrderConfirmation"))
);
const InventoryDiamondList = Loadable(
  lazy(() => import("../pages/InventoryDiamondList"))
);
const InventoryGemstoneList = Loadable(
  lazy(() => import("../pages/InventoryGemstoneList"))
);
const ProductList = Loadable(lazy(() => import("../pages/ProductList")));
const MasterCardList = Loadable(lazy(() => import("../pages/MasterCardList")));
const InventoryUnmountList = Loadable(
  lazy(() => import("../pages/InventoryUnmountList"))
);
const InventorySetmountList = Loadable(
  lazy(() => import("../pages/InventorySetmountList"))
);
const InventoryEarringsList = Loadable(
  lazy(() => import("../pages/InventoryEarringsList"))
);
const InventoryPendantsList = Loadable(
  lazy(() => import("../pages/InventoryPendantsList"))
);
const InventoryBraceletList = Loadable(
  lazy(() => import("../pages/InventoryBraceletList"))
);
const InventoryNaturalDiamondList = Loadable(
  lazy(() => import("../pages/InventoryNaturalDiamondList"))
);
const InventoryNaturalGemstoneList = Loadable(
  lazy(() => import("../pages/InventoryNaturalGemstoneList"))
);
const InventoryNaturalBraceletList = Loadable(
  lazy(() => import("../pages/InventoryNaturalBraceletList"))
);
const InventoryNaturalUnmountList = Loadable(
  lazy(() => import("../pages/InventoryNaturalUnmountList"))
);
const InventoryNaturalSetmountList = Loadable(
  lazy(() => import("../pages/InventoryNaturalSetmountList"))
);
const InventoryNaturalEarringsList = Loadable(
  lazy(() => import("../pages/InventoryNaturalEarringsList"))
);
const InventoryNaturalPendantsList = Loadable(
  lazy(() => import("../pages/InventoryNaturalPendantsList"))
);
const DesignDetailsList = Loadable(
  lazy(() => import("../pages/DesignDetailsList"))
);
const Calendar = Loadable(lazy(() => import("../pages/Calendar")));
const LeaderList = Loadable(lazy(() => import("../pages/LeaderList")));
const LeaderCreate = Loadable(lazy(() => import("../pages/LeaderCreate")));
const LeaderFormView = Loadable(lazy(() => import("../pages/LeaderFormView")));
const LeaderCalender = Loadable(lazy(() => import("../pages/LeaderCalender")));
const DataReportsList = Loadable(
  lazy(() => import("../pages/DataReportsList"))
);
const MonthlyDataReports = Loadable(
  lazy(() => import("../pages/MonthlyDataReports"))
);
const AnnualDataReports = Loadable(
  lazy(() => import("../pages/AnnualDataReports"))
);
const MainQuotationView = Loadable(
  lazy(() => import("../pages/MainQuotationView"))
);

const EngagementRingReport = Loadable(
  lazy(() => import("../pages/EngagementRingReport"))
);

// Components
const Alert = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Alert"))
);
const Autocomplete = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Autocomplete"))
);
const Avatar = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Avatar"))
);
const Badge = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Badge"))
);
const Breadcrumb = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Breadcrumb"))
);
const Buttons = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/buttons"))
);
const Checkbox = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Checkboxes"))
);
const Chip = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/chips"))
);
const Dialog = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/dialog"))
);
const Label = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Label"))
);
const List = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Lists"))
);
const Menu = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Menus"))
);
const Pagination = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Pagination"))
);
const Pickers = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/pickers"))
);
const Popover = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Popover"))
);
const Progress = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/progress"))
);
const RadioButtons = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/RadioButtons"))
);
const Rating = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Rating"))
);
const Slider = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Slider"))
);
const Snackbar = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Snackbar"))
);
const Stepper = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/stepper"))
);
const Switches = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Switches"))
);
const Table = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/table"))
);
const Tabs = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Tabs"))
);
const Textfield = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/textfield"))
);
const Timeline = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Timeline"))
);
const Tooltip = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/Tooltip"))
);
const TransferList = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/transfer-list"))
);
const TreeView = Loadable(
  lazy(() => import("../pages/components-overview/material-ui/TreeView"))
);
